export type SocialAspect = 'video' | 'instagram' | 'portrait';
export type SocialPlatform = 'instagram' | 'tiktok' | 'youtube';

export type SocialEmbed = {
	platform: SocialPlatform;
	/** Built from a fixed template and an extracted id, never from the input. */
	embedUrl: string;
	aspect: SocialAspect;
	/** Whether the post is a video. Drives the glyph, never the frame. */
	isVideo: boolean;
};

const INSTAGRAM_HOSTS = new Set([
	'instagram.com',
	'www.instagram.com',
	'm.instagram.com',
	'instagr.am',
	'www.instagr.am',
]);
const TIKTOK_HOSTS = new Set(['tiktok.com', 'www.tiktok.com', 'm.tiktok.com']);
const YOUTUBE_HOSTS = new Set([
	'youtube.com',
	'www.youtube.com',
	'm.youtube.com',
	'music.youtube.com',
]);
const YOUTUBE_SHORT_HOSTS = new Set(['youtu.be', 'www.youtu.be']);

// An Instagram permalink may carry a leading /<username> segment, which the
// Instagram app produces. See save_inspiration.py classify_post_url.
const INSTAGRAM_PATH = /^(?:\/[^/]+)?\/(p|reel|reels|tv)\/([A-Za-z0-9_-]+)\/?$/;
const TIKTOK_PATH = /^\/@[^/]+\/(video|photo)\/(\d+)\/?$/;
const TIKTOK_LEGACY_PATH = /^\/v\/(\d+)\.html$/;
const YOUTUBE_PATH = /^\/(shorts|live|embed|v)\/([A-Za-z0-9_-]{11})\/?$/;
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

const INSTAGRAM_EMBED_SEGMENT: Record<string, string> = {
	p: 'p',
	reel: 'reel',
	reels: 'reel',
	tv: 'tv',
};

const instagramEmbed = (url: URL): SocialEmbed | null => {
	const match = INSTAGRAM_PATH.exec(url.pathname);
	if (!match) return null;
	const segment = INSTAGRAM_EMBED_SEGMENT[match[1]];
	if (!segment) return null;
	return {
		platform: 'instagram',
		embedUrl: `https://www.instagram.com/${segment}/${match[2]}/embed`,
		aspect: 'instagram',
		isVideo: segment !== 'p',
	};
};

const tiktokEmbed = (url: URL): SocialEmbed | null => {
	const match = TIKTOK_PATH.exec(url.pathname);
	const legacy = match ? null : TIKTOK_LEGACY_PATH.exec(url.pathname);
	const id = match?.[2] ?? legacy?.[1];
	// A vm./vt./`/t/` share link carries no numeric id, so no player URL exists.
	if (!id) return null;
	return {
		platform: 'tiktok',
		embedUrl: `https://www.tiktok.com/player/v1/${id}?autoplay=1&music_info=1&description=0`,
		aspect: 'portrait',
		isVideo: match ? match[1] === 'video' : true,
	};
};

const youtubeUrl = (id: string): string =>
	`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;

const youtubeEmbed = (url: URL): SocialEmbed | null => {
	if (YOUTUBE_SHORT_HOSTS.has(url.hostname)) {
		const id = url.pathname.slice(1);
		if (!YOUTUBE_ID.test(id)) return null;
		return {
			platform: 'youtube',
			embedUrl: youtubeUrl(id),
			aspect: 'video',
			isVideo: true,
		};
	}
	if (url.pathname === '/watch') {
		const id = url.searchParams.get('v') ?? '';
		if (!YOUTUBE_ID.test(id)) return null;
		return {
			platform: 'youtube',
			embedUrl: youtubeUrl(id),
			aspect: 'video',
			isVideo: true,
		};
	}
	const match = YOUTUBE_PATH.exec(url.pathname);
	if (!match) return null;
	return {
		platform: 'youtube',
		embedUrl: youtubeUrl(match[2]),
		aspect: match[1] === 'shorts' ? 'portrait' : 'video',
		isVideo: true,
	};
};

/**
 * Read a saved inspiration's `source_url` and return what it takes to embed it.
 *
 * The returned `embedUrl` is always built from a fixed template and an id that
 * matched a charset-restricted pattern. The caller's URL never reaches an
 * iframe `src`. Returns null when no embed can be built, and the caller then
 * shows the cover picture and a link out.
 */
export function getSocialEmbed(sourceUrl: string): SocialEmbed | null {
	let url: URL;
	try {
		url = new URL(sourceUrl);
	} catch {
		return null;
	}
	// The ingest gate accepts http, so old records carry it. The template is
	// always https, so upgrading here strands nothing and downgrades nothing.
	if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
	const host = url.hostname.toLowerCase();
	if (INSTAGRAM_HOSTS.has(host)) return instagramEmbed(url);
	if (TIKTOK_HOSTS.has(host)) return tiktokEmbed(url);
	if (YOUTUBE_HOSTS.has(host) || YOUTUBE_SHORT_HOSTS.has(host)) {
		return youtubeEmbed(url);
	}
	return null;
}

type FrameStyle = { root: string; frame: string };

// The root cap is the frame cap plus two 12px paddings plus two 1px borders.
// The root carries the width because glass-card-sm sets none, and a percentage
// width resolves to auto during intrinsic sizing — so without it the caption
// string would decide the video size.
const FRAME_STYLES: Record<SocialAspect, FrameStyle> = {
	video: { root: 'max-w-[586px]', frame: 'aspect-video max-w-[560px]' },
	// 585 = 54px header + 420px media + about 111px of footer: the "View more
	// on Instagram" row, its divider, and the like/comment/share bar. The old
	// 515 counted only a 41px action bar, so the bar was sliced in half at every
	// width. The chrome is a FIXED pixel height while the media scales, so no
	// ratio fits every width exactly; this one errs toward whitespace, because
	// spare space reads as padding and a short frame reads as broken.
	instagram: { root: 'max-w-[362px]', frame: 'aspect-[336/585] max-w-[336px]' },
	// 336 to match Instagram's frame. A TikTok and a YouTube short are the same
	// vertical video as a Reel, so they get the same width; only the height
	// differs, because these players carry no chrome and keep a true 9:16.
	portrait: { root: 'max-w-[362px]', frame: 'aspect-[9/16] max-w-[336px]' },
};

export const NO_EMBED_FRAME_STYLE: FrameStyle = {
	root: 'max-w-[362px]',
	frame: 'aspect-[4/5] max-w-[336px]',
};

export function getFrameStyle(embed: SocialEmbed | null): FrameStyle {
	return embed ? FRAME_STYLES[embed.aspect] : NO_EMBED_FRAME_STYLE;
}

// The same caps as numbers. A grouped set has to compute a max across its
// members and hand a slide a definite width, and Tailwind needs the literal
// class string, so the two forms have to live together. A unit test parses the
// class strings and asserts they still agree with these numbers.
export const FRAME_ROOT_CAP_PX: Record<SocialAspect, number> = {
	video: 586,
	instagram: 362,
	portrait: 362,
};

export const NO_EMBED_ROOT_CAP_PX = 362;

export function getFrameRootCapPx(embed: SocialEmbed | null): number {
	return embed ? FRAME_ROOT_CAP_PX[embed.aspect] : NO_EMBED_ROOT_CAP_PX;
}
